import React from 'react';
import Moment from 'moment';
import { t } from '../../../../i18n';
import CustomDataTable from '../../shared/CustomDataTable';
import { NoDataRanking } from '../../normalizer';

/**
 * Format points for display (remove decimals if whole number)
 */
const formatPoints = (points) => {
  const parsed = parseFloat(points);
  if (isNaN(parsed)) return '0';
  return parsed % 1 === 0
    ? parsed.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    : parsed.toLocaleString('pt-PT');
};

/**
 * Main BalanceTable component using CustomDataTable
 */
const BalanceTable = (props) => {
  const {
    points = [],
    rankingLabel,
    totalPages = 1,
    page = 1,
    onPageChange,
  } = props;

  const pointsLabel = rankingLabel && rankingLabel.currencyLabel
    ? rankingLabel.currencyLabel
    : t('gamification2026.rankings.points');

  // When there are no entries, render the empty state outside the table so
  // headers and the table card surface are not shown.
  if (!points || points.length === 0) {
    return (
      <div className="balance-table balance-table--empty">
        <div className="balance-table__empty-state">
          <h5 className="balance-table__empty-state-title">
            {t('gamification2026.balance.no-results-title')}
          </h5>
          <p className="balance-table__empty-state-text">
            {t('gamification2026.balance.no-results-description')}
          </p>
          <NoDataRanking className="balance-table__empty-state-icon" />
        </div>
      </div>
    );
  }

  // Column definitions
  const columns = [
    {
      key: 'description',
      label: t('gamification2026.balance.description'),
      sortKey: 'description',
      render: (_v, item) => item.description || '-',
    },
    {
      key: 'date',
      label: t('gamification2026.balance.date'),
      sortKey: 'date',
      render: (_v, item) => Moment(item.date).format(t('date-format')),
    },
    {
      key: 'points',
      label: pointsLabel,
      sortKey: 'points',
      render: (_v, item) => formatPoints(item.points),
    },
  ];

  // Sort function
  const sortFn = (data, sort) => {
    return [...data].sort((a, b) => {
      const multiplier = sort.direction === 'asc' ? 1 : -1;
      if (sort.key === 'date') {
        return (new Date(a.date) - new Date(b.date)) * multiplier;
      }
      if (sort.key === 'points') {
        return (parseFloat(a.points) - parseFloat(b.points)) * multiplier;
      }
      if (sort.key === 'description') {
        const descA = (a.description || '').toLowerCase();
        const descB = (b.description || '').toLowerCase();
        return descA.localeCompare(descB) * multiplier;
      }
      return 0;
    });
  };

  // Custom row renderer
  const renderRow = (item, index, className) => {
    const validDescription = item.description || '-';
    const formattedDate = Moment(item.date).format(t('date-format'));
    const formattedPoints = formatPoints(item.points);

    return (
      <tr key={index} className={`data-table__row ${className}__row`}>
        <td className={`data-table__cell ${className}__cell ${className}__cell--description`}>
          {validDescription}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--date`}>
          {formattedDate}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--points`}>
          {formattedPoints}
        </td>
      </tr>
    );
  };

  return (
    <CustomDataTable
      className="balance-table"
      columns={columns}
      data={points}
      renderRow={renderRow}
      rowKey={(_, index) => index}
      defaultSort={{ key: 'date', direction: 'desc' }}
      sortFn={sortFn}
      pagination={{
        totalPages,
        currentPage: page,
        onPageChange,
      }}
    />
  );
};

export default BalanceTable;